import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import apiClient from '../utils/api';
import { Button } from '../components/ui/button';
import { Dumbbell, Plus, Trash2, Edit2, Users, MapPin, Tag, Repeat, Copy, CreditCard, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { format } from 'date-fns';

// Import admin components
import {
  AdminLocations,
  AdminDiscounts,
  AdminBookings,
  AdminRecurringForm,
  AdminClassForm,
  AdminImageLibrary,
  AdminUsers,
  AdminEmailMarketing
} from '../components/admin';

const Admin = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [classes, setClasses] = useState([]);
  const [classesLoading, setClassesLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingClass, setEditingClass] = useState(null);
  const [selectedClassBookings, setSelectedClassBookings] = useState(null);
  const [showBookings, setShowBookings] = useState(false);
  
  // Section visibility states
  const [showLocationManager, setShowLocationManager] = useState(false);
  const [showDiscountManager, setShowDiscountManager] = useState(false);
  const [showRecurringForm, setShowRecurringForm] = useState(false);
  const [showBookingsManager, setShowBookingsManager] = useState(false);
  const [showImageLibrary, setShowImageLibrary] = useState(false);
  const [showUserManager, setShowUserManager] = useState(false);
  const [showEmailMarketing, setShowEmailMarketing] = useState(false);
  
  // Location state
  const [locations, setLocations] = useState([]);
  const [newLocationName, setNewLocationName] = useState('');
  const [newLocationAddress, setNewLocationAddress] = useState('');
  
  // Discount code state
  const [discountCodes, setDiscountCodes] = useState([]);
  const [newDiscountCode, setNewDiscountCode] = useState('');
  const [newDiscountType, setNewDiscountType] = useState('percentage');
  const [newDiscountValue, setNewDiscountValue] = useState('');
  const [newDiscountLimit, setNewDiscountLimit] = useState('');
  const [newDiscountValidFrom, setNewDiscountValidFrom] = useState('');
  const [newDiscountValidUntil, setNewDiscountValidUntil] = useState('');
  
  // Recurring class state
  const [recurringData, setRecurringData] = useState({
    title: '',
    description: '',
    instructor: '',
    location: 'Main Studio',
    start_datetime: '',
    duration: 60,
    capacity: 20,
    price: 20,
    image_url: '',
    recurrence_type: 'weekly',
    specific_days: [],
    weeks_in_advance: 4
  });
  
  // Image library state
  const [imageLibrary, setImageLibrary] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [imageSelectTarget, setImageSelectTarget] = useState(null);
  const fileInputRef = useRef(null);
  
  // Bookings & Refunds state
  const [allBookings, setAllBookings] = useState([]);
  const [refundedBookings, setRefundedBookings] = useState([]);
  const [bookingsLoading, setBookingsLoading] = useState(false);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [refundingBooking, setRefundingBooking] = useState(null);
  const [refundReason, setRefundReason] = useState('');
  const [refundAmount, setRefundAmount] = useState('');
  const [isFullRefund, setIsFullRefund] = useState(true);
  const [processingRefund, setProcessingRefund] = useState(false);
  const [showRefundedTab, setShowRefundedTab] = useState(false);
  
  // Class form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    instructor: '',
    location: 'Main Studio',
    class_datetime: '',
    duration: 60,
    capacity: 20,
    price: 20,
    image_url: ''
  });

  const defaultImages = [
    'https://images.unsplash.com/photo-1758274535024-be3faa30f507',
    'https://images.unsplash.com/photo-1627262899263-839fe9307c00',
    'https://images.unsplash.com/photo-1602827114685-efbb2717da9f',
    'https://images.unsplash.com/photo-1758875569517-559a631fb44d'
  ];

  // Fetch functions wrapped in useCallback
  const fetchClasses = useCallback(async () => {
    try {
      const response = await apiClient.get('/classes');
      setClasses(response.data);
    } catch (error) {
      toast.error('Failed to load classes');
    } finally {
      setClassesLoading(false);
    }
  }, []);

  const fetchLocations = useCallback(async () => {
    try {
      const response = await apiClient.get('/locations');
      setLocations(response.data);
    } catch (error) {
      console.error('Failed to load locations:', error.message);
    }
  }, []);

  const fetchDiscountCodes = useCallback(async () => {
    try {
      const response = await apiClient.get('/discount-codes');
      setDiscountCodes(response.data);
    } catch (error) {
      console.error('Failed to load discount codes:', error.message);
    }
  }, []);

  const fetchImageLibrary = useCallback(async () => {
    try {
      const response = await apiClient.get('/images/library');
      setImageLibrary(response.data);
    } catch (error) {
      console.error('Failed to load image library:', error.message);
    }
  }, []);

  useEffect(() => {
    if (authLoading) return;
    
    if (!user || user.role !== 'admin') {
      navigate('/dashboard');
      return;
    }
    fetchClasses();
    fetchLocations();
    fetchDiscountCodes();
    fetchImageLibrary();
  }, [user, authLoading, navigate, fetchClasses, fetchLocations, fetchDiscountCodes, fetchImageLibrary]);

  const fetchAllBookings = async () => {
    setBookingsLoading(true);
    try {
      const [activeRes, refundedRes] = await Promise.all([
        apiClient.get('/admin/bookings'),
        apiClient.get('/admin/bookings/refunded')
      ]);
      setAllBookings(activeRes.data);
      setRefundedBookings(refundedRes.data);
    } catch (error) {
      toast.error('Failed to load bookings');
    } finally {
      setBookingsLoading(false);
    }
  };

  // Location handlers
  const handleAddLocation = async (e) => {
    e.preventDefault();
    if (!newLocationName.trim()) {
      toast.error('Location name is required');
      return;
    }
    try {
      await apiClient.post('/locations', { 
        name: newLocationName.trim(),
        address: newLocationAddress.trim() 
      });
      toast.success('Location added successfully');
      setNewLocationName('');
      setNewLocationAddress('');
      fetchLocations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add location');
    }
  };

  const handleDeleteLocation = async (locationId, locationName) => {
    if (!window.confirm(`Are you sure you want to delete "${locationName}"?`)) return;
    try {
      await apiClient.delete(`/locations/${locationId}`);
      toast.success('Location deleted successfully');
      fetchLocations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete location');
    }
  };

  // Discount handlers
  const handleAddDiscountCode = async (e) => {
    e.preventDefault();
    if (!newDiscountCode.trim() || !newDiscountValue) {
      toast.error('Code and discount value are required');
      return;
    }
    try {
      await apiClient.post('/discount-codes', {
        code: newDiscountCode.trim().toUpperCase(),
        discount_type: newDiscountType,
        discount_value: parseFloat(newDiscountValue),
        usage_limit: newDiscountLimit ? parseInt(newDiscountLimit) : null,
        valid_from: newDiscountValidFrom ? new Date(newDiscountValidFrom).toISOString() : null,
        valid_until: newDiscountValidUntil ? new Date(newDiscountValidUntil).toISOString() : null
      });
      toast.success('Discount code created successfully');
      setNewDiscountCode('');
      setNewDiscountValue('');
      setNewDiscountLimit('');
      setNewDiscountValidFrom('');
      setNewDiscountValidUntil('');
      fetchDiscountCodes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create discount code');
    }
  };

  const handleDeleteDiscountCode = async (codeId, code) => {
    if (!window.confirm(`Are you sure you want to delete code "${code}"?`)) return;
    try {
      await apiClient.delete(`/discount-codes/${codeId}`);
      toast.success('Discount code deleted successfully');
      fetchDiscountCodes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete discount code');
    }
  };

  // Recurring class handler
  const handleRecurringSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await apiClient.post('/classes/recurring', recurringData);
      toast.success(response.data.message);
      setShowRecurringForm(false);
      setRecurringData({
        title: '',
        description: '',
        instructor: '',
        location: 'Main Studio',
        start_datetime: '',
        duration: 60,
        capacity: 20,
        price: 20,
        image_url: '',
        recurrence_type: 'weekly',
        specific_days: [],
        weeks_in_advance: 4
      });
      fetchClasses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create recurring classes');
    }
  };

  // Class form handlers
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingClass) {
        await apiClient.put(`/classes/${editingClass.class_id}`, formData);
        toast.success('Class updated successfully');
      } else {
        await apiClient.post('/classes', formData);
        toast.success('Class created successfully');
      }
      resetForm();
      fetchClasses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save class');
    }
  };

  const handleEdit = (cls) => {
    setEditingClass(cls);
    setFormData({
      title: cls.title,
      description: cls.description,
      instructor: cls.instructor,
      location: cls.location || 'Main Studio',
      class_datetime: new Date(cls.class_datetime).toISOString().slice(0, 16),
      duration: cls.duration,
      capacity: cls.capacity,
      price: cls.price,
      image_url: cls.image_url
    });
    setShowForm(true);
  };

  const handleDelete = async (classId) => {
    if (!window.confirm('Are you sure you want to delete this class?')) return;
    try {
      await apiClient.delete(`/classes/${classId}`);
      toast.success('Class deleted successfully');
      fetchClasses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete class');
    }
  };

  const handleCopyClass = async (classId) => {
    try {
      const response = await apiClient.post(`/classes/${classId}/copy`);
      toast.success('Class copied! Edit the copy to update date/time.');
      fetchClasses();
      handleEdit(response.data.class);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to copy class');
    }
  };

  const viewBookings = async (classId, classTitle) => {
    try {
      const response = await apiClient.get(`/classes/${classId}/bookings`);
      setSelectedClassBookings({ classId, classTitle, bookings: response.data });
      setShowBookings(true);
    } catch (error) {
      toast.error('Failed to load bookings');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      instructor: '',
      location: 'Main Studio',
      class_datetime: '',
      duration: 60,
      capacity: 20,
      price: 20,
      image_url: ''
    });
    setEditingClass(null);
    setShowForm(false);
  };

  // Image handlers
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Allowed: JPEG, PNG, WebP, GIF');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size: 5MB');
      return;
    }
    
    setUploading(true);
    const uploadFormData = new FormData();
    uploadFormData.append('file', file);
    
    try {
      const response = await apiClient.post('/upload/image', uploadFormData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Image uploaded successfully');
      fetchImageLibrary();
      
      const imageUrl = `${process.env.REACT_APP_BACKEND_URL}${response.data.url}`;
      if (imageSelectTarget === 'recurring') {
        setRecurringData({ ...recurringData, image_url: imageUrl });
      } else {
        setFormData({ ...formData, image_url: imageUrl });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDeleteImage = async (imageId) => {
    if (!window.confirm('Delete this image from the library?')) return;
    try {
      await apiClient.delete(`/images/${imageId}`);
      toast.success('Image deleted');
      fetchImageLibrary();
    } catch (error) {
      toast.error('Failed to delete image');
    }
  };

  const selectImageFromLibrary = (url) => {
    const fullUrl = url.startsWith('http') ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`;
    if (imageSelectTarget === 'recurring') {
      setRecurringData({ ...recurringData, image_url: fullUrl });
    } else {
      setFormData({ ...formData, image_url: fullUrl });
    }
    setShowImageLibrary(false);
  };

  const openImageLibrary = (target) => {
    setImageSelectTarget(target);
    setShowImageLibrary(true);
  };

  // Bookings handlers
  const toggleBookingsManager = () => {
    if (!showBookingsManager) {
      fetchAllBookings();
    }
    setShowBookingsManager(!showBookingsManager);
  };

  const openRefundModal = (booking) => {
    setRefundingBooking(booking);
    setRefundReason('');
    setRefundAmount(booking.final_price?.toString() || '');
    setIsFullRefund(true);
    setShowRefundModal(true);
  };

  const handleProcessRefund = async () => {
    if (!refundReason.trim()) {
      toast.error('Please provide a reason for the refund');
      return;
    }
    
    const amount = isFullRefund ? null : parseFloat(refundAmount);
    if (!isFullRefund && (isNaN(amount) || amount <= 0)) {
      toast.error('Please enter a valid refund amount');
      return;
    }
    
    setProcessingRefund(true);
    try {
      const payload = {
        booking_id: refundingBooking.booking_id,
        reason: refundReason.trim(),
        refund_amount: isFullRefund ? null : amount
      };
      
      const response = await apiClient.post('/admin/bookings/refund', payload);
      toast.success(`Refund of £${response.data.refund_amount.toFixed(2)} processed successfully`);
      setShowRefundModal(false);
      fetchAllBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process refund');
    } finally {
      setProcessingRefund(false);
    }
  };

  if (authLoading || classesLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Navbar */}
      <nav className="fixed top-0 w-full z-50 bg-background/80 backdrop-blur-md border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center gap-2">
            <Dumbbell className="w-8 h-8 text-primary" />
            <span className="font-heading text-2xl font-bold tracking-tight">T-Fit Inc Classes Admin</span>
          </Link>
          <div className="flex gap-4">
            <Link to="/dashboard">
              <Button variant="ghost" data-testid="nav-dashboard-btn">Dashboard</Button>
            </Link>
          </div>
        </div>
      </nav>

      <div className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-6">
          {/* Header */}
          <div className="flex justify-between items-center mb-12">
            <div>
              <h1 className="font-heading text-5xl font-black mb-2" data-testid="admin-title">
                CLASS <span className="text-primary">MANAGEMENT</span>
              </h1>
              <p className="text-lg text-neutral-300">Create and manage fitness classes</p>
            </div>
            <div className="flex gap-4 flex-wrap">
              <Button
                variant="outline"
                onClick={() => setShowUserManager(!showUserManager)}
                className="border-white/20"
                data-testid="manage-users-btn"
              >
                <Users className="w-4 h-4 mr-2" />
                USERS
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowLocationManager(!showLocationManager)}
                className="border-white/20"
                data-testid="manage-locations-btn"
              >
                <MapPin className="w-4 h-4 mr-2" />
                LOCATIONS
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowDiscountManager(!showDiscountManager)}
                className="border-white/20"
                data-testid="manage-discounts-btn"
              >
                <Tag className="w-4 h-4 mr-2" />
                DISCOUNTS
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowRecurringForm(!showRecurringForm)}
                className="border-white/20"
                data-testid="recurring-class-btn"
              >
                <Repeat className="w-4 h-4 mr-2" />
                RECURRING
              </Button>
              <Button
                variant="outline"
                onClick={toggleBookingsManager}
                className="border-white/20"
                data-testid="manage-bookings-btn"
              >
                <CreditCard className="w-4 h-4 mr-2" />
                BOOKINGS
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowEmailMarketing(!showEmailMarketing)}
                className="border-white/20"
                data-testid="email-marketing-btn"
              >
                <Mail className="w-4 h-4 mr-2" />
                EMAIL
              </Button>
              <Button
                onClick={() => setShowForm(!showForm)}
                className="bg-primary hover:bg-primary-hover font-bold"
                data-testid="add-class-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                ADD CLASS
              </Button>
            </div>
          </div>

          {/* Email Marketing */}
          {showEmailMarketing && (
            <AdminEmailMarketing onClose={() => setShowEmailMarketing(false)} />
          )}

          {/* User Manager */}
          {showUserManager && (
            <AdminUsers onClose={() => setShowUserManager(false)} />
          )}

          {/* Location Manager */}
          {showLocationManager && (
            <AdminLocations
              locations={locations}
              newLocationName={newLocationName}
              setNewLocationName={setNewLocationName}
              newLocationAddress={newLocationAddress}
              setNewLocationAddress={setNewLocationAddress}
              onAddLocation={handleAddLocation}
              onDeleteLocation={handleDeleteLocation}
              onClose={() => setShowLocationManager(false)}
            />
          )}

          {/* Discount Code Manager */}
          {showDiscountManager && (
            <AdminDiscounts
              discountCodes={discountCodes}
              newDiscountCode={newDiscountCode}
              setNewDiscountCode={setNewDiscountCode}
              newDiscountType={newDiscountType}
              setNewDiscountType={setNewDiscountType}
              newDiscountValue={newDiscountValue}
              setNewDiscountValue={setNewDiscountValue}
              newDiscountLimit={newDiscountLimit}
              setNewDiscountLimit={setNewDiscountLimit}
              newDiscountValidFrom={newDiscountValidFrom}
              setNewDiscountValidFrom={setNewDiscountValidFrom}
              newDiscountValidUntil={newDiscountValidUntil}
              setNewDiscountValidUntil={setNewDiscountValidUntil}
              onAddDiscountCode={handleAddDiscountCode}
              onDeleteDiscountCode={handleDeleteDiscountCode}
              onClose={() => setShowDiscountManager(false)}
            />
          )}

          {/* Bookings Manager */}
          {showBookingsManager && (
            <AdminBookings
              allBookings={allBookings}
              refundedBookings={refundedBookings}
              bookingsLoading={bookingsLoading}
              showRefundedTab={showRefundedTab}
              setShowRefundedTab={setShowRefundedTab}
              showRefundModal={showRefundModal}
              setShowRefundModal={setShowRefundModal}
              refundingBooking={refundingBooking}
              refundReason={refundReason}
              setRefundReason={setRefundReason}
              refundAmount={refundAmount}
              setRefundAmount={setRefundAmount}
              isFullRefund={isFullRefund}
              setIsFullRefund={setIsFullRefund}
              processingRefund={processingRefund}
              onRefresh={fetchAllBookings}
              onOpenRefundModal={openRefundModal}
              onProcessRefund={handleProcessRefund}
              onClose={() => setShowBookingsManager(false)}
            />
          )}

          {/* Recurring Class Form */}
          {showRecurringForm && (
            <AdminRecurringForm
              recurringData={recurringData}
              setRecurringData={setRecurringData}
              locations={locations}
              onSubmit={handleRecurringSubmit}
              onOpenImageLibrary={openImageLibrary}
              onClose={() => setShowRecurringForm(false)}
            />
          )}

          {/* Class Form */}
          {showForm && (
            <AdminClassForm
              formData={formData}
              setFormData={setFormData}
              editingClass={editingClass}
              locations={locations}
              uploading={uploading}
              fileInputRef={fileInputRef}
              defaultImages={defaultImages}
              onSubmit={handleSubmit}
              onOpenImageLibrary={openImageLibrary}
              onImageUpload={(e) => { setImageSelectTarget('form'); handleImageUpload(e); }}
              onReset={resetForm}
            />
          )}

          {/* Image Library Modal */}
          {showImageLibrary && (
            <AdminImageLibrary
              imageLibrary={imageLibrary}
              uploading={uploading}
              fileInputRef={fileInputRef}
              onSelectImage={selectImageFromLibrary}
              onDeleteImage={handleDeleteImage}
              onUpload={(e) => handleImageUpload(e)}
              onClose={() => setShowImageLibrary(false)}
            />
          )}

          {/* Classes List */}
          <div className="space-y-4">
            {classes.length === 0 ? (
              <div className="text-center py-16 bg-background-paper border border-white/10 rounded-sm">
                <p className="text-lg text-neutral-400">No classes created yet</p>
                <p className="text-sm text-neutral-500 mt-2">Click &quot;ADD CLASS&quot; to create your first class</p>
              </div>
            ) : (
              classes.map((cls, index) => (
                <motion.div
                  key={cls.class_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-background-paper border border-white/10 rounded-sm p-6"
                  data-testid={`admin-class-card-${index}`}
                >
                  <div className="flex gap-6">
                    <img
                      src={cls.image_url}
                      alt={cls.title}
                      className="w-32 h-32 object-cover rounded-sm"
                    />
                    <div className="flex-1">
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="font-heading text-2xl font-bold">{cls.title}</h3>
                          <p className="text-primary">{cls.instructor}</p>
                          <p className="text-sm text-neutral-400 mt-1">{cls.location || 'Main Studio'}</p>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleCopyClass(cls.class_id)}
                            className="border-white/20"
                            data-testid={`copy-class-btn-${index}`}
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEdit(cls)}
                            className="border-white/20"
                            data-testid={`edit-class-btn-${index}`}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="border-error text-error hover:bg-error hover:text-white"
                            onClick={() => handleDelete(cls.class_id)}
                            data-testid={`delete-class-btn-${index}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      <p className="text-sm text-neutral-400 mt-2 line-clamp-2">{cls.description}</p>
                      <div className="flex gap-6 mt-4 text-sm">
                        <span className="text-neutral-400">
                          <span className="text-white font-bold">{format(new Date(cls.class_datetime), 'MMM dd, yyyy')}</span>
                          {' '}at {format(new Date(cls.class_datetime), 'h:mm a')}
                        </span>
                        <span className="text-neutral-400">
                          <span className="text-white font-bold">{cls.duration}</span> min
                        </span>
                        <span className="text-neutral-400">
                          <span className="text-white font-bold">{cls.spots_available}</span>/{cls.capacity} spots
                        </span>
                        <span className="text-neutral-400">
                          <span className="text-primary font-bold">£{cls.price}</span>
                        </span>
                      </div>
                      <Button
                        variant="link"
                        size="sm"
                        className="text-primary mt-2 p-0"
                        onClick={() => viewBookings(cls.class_id, cls.title)}
                        data-testid={`view-bookings-btn-${index}`}
                      >
                        View Bookings ({cls.capacity - cls.spots_available})
                      </Button>
                    </div>
                  </div>
                </motion.div>
              ))
            )}
          </div>

          {/* Class Bookings Modal */}
          {showBookings && selectedClassBookings && (
            <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-background-paper border border-white/10 rounded-sm p-8 max-w-2xl w-full max-h-[80vh] overflow-y-auto"
              >
                <div className="flex justify-between items-center mb-6">
                  <h3 className="font-heading text-2xl font-bold">
                    BOOKINGS: <span className="text-primary">{selectedClassBookings.classTitle}</span>
                  </h3>
                  <Button variant="ghost" onClick={() => setShowBookings(false)}>
                    ✕
                  </Button>
                </div>
                {selectedClassBookings.bookings.length === 0 ? (
                  <p className="text-neutral-400 text-center py-8">No bookings yet</p>
                ) : (
                  <div className="space-y-4">
                    {selectedClassBookings.bookings.map((booking, i) => (
                      <div 
                        key={booking.booking_id} 
                        className="flex justify-between items-center p-4 bg-background rounded-sm border border-white/10"
                      >
                        <div>
                          <p className="font-bold">{booking.user_name}</p>
                          <p className="text-sm text-neutral-400">{booking.user_email}</p>
                          <p className="text-xs text-neutral-500 mt-1">
                            Booked: {format(new Date(booking.booking_date), 'MMM dd, yyyy h:mm a')}
                            {booking.spots > 1 && ` • ${booking.spots} spots`}
                          </p>
                        </div>
                        <div className="text-right">
                          <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                            booking.payment_status === 'completed' 
                              ? 'bg-success/20 text-success' 
                              : 'bg-warning/20 text-warning'
                          }`}>
                            {booking.payment_status?.toUpperCase()}
                          </span>
                          <p className="text-xs text-neutral-500 mt-1 capitalize">
                            {booking.payment_method || 'N/A'}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </motion.div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Admin;
